import { useState } from "react"
import { mergeAttributes, Node } from "@tiptap/core"
import { Plugin, PluginKey, Selection } from "@tiptap/pm/state"
import {
	NodeViewContent,
	NodeViewWrapper,
	ReactNodeViewRenderer,
} from "@tiptap/react"

import type { NodeViewProps } from "@tiptap/react"
import { EllipsisIcon as Ellipsis } from "../../../icons/project/ellipsis-icon"
import { Button } from "../../button"
import { Tooltip } from "../../tooltip"

const EmailHistoryComponent = ({ node, updateAttributes }: NodeViewProps) => {
	const isExpandedAttr = node.attrs.isExpanded as boolean | undefined
	const [isExpanded, setIsExpanded] = useState(isExpandedAttr ?? false)

	const toggleExpanded = () => {
		const newState = !isExpanded
		setIsExpanded(newState)
		updateAttributes({ isExpanded: newState })
	}

	return (
		<NodeViewWrapper className="email-history-wrapper my-1">
			<div className="flex flex-col gap-4">
				<Tooltip
					label={
						isExpanded ? "Collapse trimmed content" : "Show trimmed content"
					}
				>
					<Button
						onClick={toggleExpanded}
						variant="ghost"
						className="-ml-1 h-4 max-h-4 min-h-4 w-fit rounded-full p-1 text-tertiary hover:text-primary"
					>
						<Ellipsis className="size-4 min-h-4 min-w-4" />
					</Button>
				</Tooltip>
				{isExpanded && (
					<div className="email-history-content">
						<NodeViewContent />
					</div>
				)}
			</div>
		</NodeViewWrapper>
	)
}

export interface EmailHistoryOptions {
	HTMLAttributes: Record<string, string>
}

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		emailHistory: {
			setEmailHistory: () => ReturnType
		}
	}
}

const EmailHistory = Node.create<EmailHistoryOptions>({
	name: "emailHistory",

	group: "block",

	content: "block+",

	defining: true,

	addOptions() {
		return {
			HTMLAttributes: {},
		}
	},

	addAttributes() {
		return {
			isExpanded: {
				default: false,
				parseHTML: (element) =>
					element.getAttribute("data-expanded") === "true",
				renderHTML: (attributes: { isExpanded?: boolean }) => {
					if (!attributes.isExpanded) {
						return {}
					}
					return {
						"data-expanded": String(attributes.isExpanded),
					}
				},
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: 'div[data-email-history="true"]',
			},
			{
				tag: 'blockquote[data-history="true"]',
			},
		]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
				"data-email-history": "true",
				class: "email-history",
			}),
			0,
		]
	},

	addNodeView() {
		return ReactNodeViewRenderer(EmailHistoryComponent)
	},

	// A collapsed history renders no contentDOM, so a caret inside it is
	// invisible and anything typed or pasted lands buried in the quoted mail.
	addProseMirrorPlugins() {
		const type = this.type
		return [
			new Plugin({
				key: new PluginKey("emailHistorySelectionGuard"),
				appendTransaction: (_transactions, _oldState, newState) => {
					const { selection, doc } = newState
					if (!selection.empty) return null
					const { $from } = selection
					let historyDepth: number | null = null
					for (let depth = 1; depth <= $from.depth; depth++) {
						const node = $from.node(depth)
						if (node.type === type && node.attrs.isExpanded !== true) {
							historyDepth = depth
							break
						}
					}
					if (historyDepth === null) return null
					const escaped =
						Selection.findFrom(
							doc.resolve($from.before(historyDepth)),
							-1,
							true,
						) ??
						Selection.findFrom(doc.resolve($from.after(historyDepth)), 1, true)
					if (!escaped) return null
					return newState.tr.setSelection(escaped)
				},
			}),
		]
	},

	addCommands() {
		return {
			setEmailHistory:
				() =>
				({ commands }) => {
					return commands.wrapIn(this.name)
				},
		}
	},
})

export { EmailHistory }
export default EmailHistory
